import { useMemo, useState } from 'react';

import { useCreditCardContext } from 'contexts/CreditCardDetailsContextProvider';
import { useCheckoutContext } from 'contexts/CheckoutContextProvider';
import useCreateSetupIntentAndStripeSetupIntent from 'hooks/useCreateSetupIntentAndStripeSetupIntent';
import useGetStripePaymentMethod from 'hooks/queries/useGetStripePaymentMethod';
import { addErrorToDatadog } from 'lib/datadogError';

import { BILLING_ADDRESS, BUSINESS_ADDRESS } from '../constants';

export const useSetUpPurchaseIntent = (trackEvent) => {
  const { handleStripeAndSetupIntent, stripeNotLoaded } =
    useCreateSetupIntentAndStripeSetupIntent(true);
  const { validateCreditCardFields } = useCreditCardContext() || {};
  const getStripePaymentMethod = useGetStripePaymentMethod();
  const {
    billingAddress = {},
    businessAddress = {},
    selectedBillingAddress,
    setSetUpIntentData,
    setStripeDetails,
    setStripeSetUpIntent,
    userDetails = {},
  } = useCheckoutContext();

  const [error, setError] = useState(null);
  const [stripeError, setStripeError] = useState(null);

  const postalCode = useMemo(() => {
    // logic to handle which postal code to use
    const billingPostalCode = billingAddress.zip;
    const businessPostalCode = businessAddress.zip;

    if (selectedBillingAddress === BUSINESS_ADDRESS && !!businessPostalCode) {
      return businessPostalCode;
    }

    if (selectedBillingAddress === BILLING_ADDRESS && !!billingPostalCode) {
      return billingPostalCode;
    }

    return billingPostalCode || businessPostalCode || '';
  }, [billingAddress.zip, businessAddress.zip, selectedBillingAddress]);

  const setPurchaseError = (error, isStripeError = false) => {
    const errorStringified = JSON.stringify(error);
    const errorMessageForTracking = `selectPlanCheckout: ${errorStringified}`;
    let errorMessage = '';

    if (isStripeError) {
      errorMessage = error?.message;
      setStripeError(errorMessage);
    } else {
      addErrorToDatadog(errorMessageForTracking);
      errorMessage = 'Issue saving payment details.';
      setError(errorMessage);
    }

    trackEvent &&
      trackEvent(`selectPlanCheckout.paymentInfo.error`, {
        error: errorMessageForTracking,
      });
  };

  const createSetUpIntents = async () => {
    if (stripeNotLoaded) {
      return;
    }

    const isValid = validateCreditCardFields() && !!postalCode;

    if (isValid) {
      try {
        const results = await handleStripeAndSetupIntent({
          ...userDetails,
          postalCode,
        });
        const {
          createSetupIntentError,
          setUpIntentData,
          stripeSetUpIntent,
          stripeError,
        } = results || {};

        if (createSetupIntentError) {
          return setPurchaseError(createSetupIntentError.message);
        }

        if (stripeError) {
          return setPurchaseError(stripeError, true);
        }

        const stripePaymentMethodId = stripeSetUpIntent.payment_method;
        const paymentData = await getStripePaymentMethod(stripePaymentMethodId);

        return {
          paymentData: { ...paymentData, stripePaymentMethodId },
          setUpIntentData,
          stripeSetUpIntent,
        };
      } catch (error) {
        setPurchaseError(error);
      }
    }
  };

  const handleSetUpIntentData = async () => {
    setError(null);
    setStripeError(null);

    try {
      const setUpData = await createSetUpIntents();
      const { paymentData, setUpIntentData, stripeSetUpIntent } =
        setUpData || {};

      if (setUpIntentData && stripeSetUpIntent && paymentData) {
        setSetUpIntentData && setSetUpIntentData(setUpIntentData);
        setStripeSetUpIntent && setStripeSetUpIntent(stripeSetUpIntent);
        setStripeDetails && setStripeDetails(paymentData);

        return true;
      }
    } catch (error) {
      setPurchaseError(error);
    }
  };

  return { createSetUpIntents, error, handleSetUpIntentData, stripeError };
};
